// Story 8.5 (ADO-2014), D-11, D-13 and D-17: the seconds left to a cut-off deadline, kept
// current.
//
//   const countdown = useCountdown(deadlineIso, offsetMs);   // null when no deadline
//
// - `offsetMs` is `serverNow - clientReceiptTime` from the shared cut-off read
//   (`useCutOffStore`), so a client clock that runs fast or slow still counts down to the
//   server's deadline.
// - `secondsLeft` is rounded up (D-17), so the shown value changes exactly when the time
//   left reaches a whole second (or minute). It ticks every second while an hour or less
//   is left and once a minute otherwise, each tick landing on that boundary. At 1h 0.5s
//   left ("1h 1m", calm) the next tick is at exactly 1h ("60m 00s", warning, per-second
//   from there), and the tone is recomputed on the same tick. A timer that fires early
//   only recomputes and reschedules for the boundary it missed. "0m 00s" appears at the
//   deadline and nothing is scheduled after it: the deadline refetch (D-11) takes over.
//   There is one pending timer at a time, cleared on unmount or a deadline change.
// - The value is right on the very render where the deadline or offset changes (a depot
//   switch never shows the old depot's time, and a fresh deadline never renders without
//   its countdown); the effect only keeps it ticking.
// - `now` is injectable (one spec makes it lag the timers); other specs use fake timers and
//   `vi.setSystemTime`. It is held in a ref read at each tick, not an effect dependency, so
//   an inline arrow (a new function every render) does not restart the tick in a loop.

import { useEffect, useRef, useState } from 'react';
import {
  cutOffTone,
  SECONDS_SHOWN_AT_OR_UNDER_SECONDS,
  type CutOffTone,
} from '../shared/cutOffDisplay';

export interface Countdown {
  readonly secondsLeft: number;
  readonly tone: CutOffTone;
}

interface StoredCountdown {
  /** The deadline and offset this countdown was computed for; null with no deadline. */
  readonly key: string | null;
  readonly countdown: Countdown | null;
}

function countdownKey(deadlineMs: number | null, offsetMs: number): string | null {
  return deadlineMs === null ? null : `${deadlineMs}|${offsetMs}`;
}

function compute(deadlineMs: number, correctedNowMs: number): Countdown {
  const secondsLeft = Math.max(0, Math.ceil((deadlineMs - correctedNowMs) / 1000));
  return { secondsLeft, tone: cutOffTone(secondsLeft) };
}

/** How long until the displayed (rounded-up) value next changes at the current cadence. */
function nextTickDelay(deadlineMs: number, correctedNowMs: number): number {
  const msLeft = deadlineMs - correctedNowMs;
  if (msLeft <= 0) return Number.POSITIVE_INFINITY;
  // An hour or less: the next whole second. Otherwise the next whole minute, which is also
  // where the one-hour and 24-hour marks fall, so "60m 00s" and "24h 0m" land on time.
  // Exactly on a boundary the value has just changed, so the next one is a period away.
  const periodMs = msLeft <= SECONDS_SHOWN_AT_OR_UNDER_SECONDS * 1000 ? 1000 : 60_000;
  const remainderMs = msLeft % periodMs;
  return remainderMs === 0 ? periodMs : remainderMs;
}

export function useCountdown(
  deadlineIso: string | null,
  offsetMs: number,
  now: () => number = Date.now,
): Countdown | null {
  const nowRef = useRef(now);
  nowRef.current = now;
  const parsedMs = deadlineIso === null ? null : Date.parse(deadlineIso);
  // An unparseable deadline counts as none, from the first render on.
  const deadlineMs = parsedMs === null || Number.isNaN(parsedMs) ? null : parsedMs;
  // The countdown is stored with the deadline and offset it was computed for. On the render
  // where either changes, the stored one belongs to the old key, so it is recomputed there
  // and then (React's "adjust state while rendering"): the first frame for a new deadline,
  // or for none, is already right, with no stale value or missing pill before the effect.
  const key = countdownKey(deadlineMs, offsetMs);
  const [stored, setStored] = useState<StoredCountdown>(() => ({
    key,
    countdown: deadlineMs === null ? null : compute(deadlineMs, now() + offsetMs),
  }));
  let current = stored;
  if (stored.key !== key) {
    current = {
      key,
      countdown: deadlineMs === null ? null : compute(deadlineMs, now() + offsetMs),
    };
    setStored(current);
  }

  useEffect(() => {
    if (deadlineMs === null) return;
    const tickKey = countdownKey(deadlineMs, offsetMs);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = () => {
      const correctedNow = nowRef.current() + offsetMs;
      setStored({ key: tickKey, countdown: compute(deadlineMs, correctedNow) });
      const delay = nextTickDelay(deadlineMs, correctedNow);
      timer = Number.isFinite(delay) ? setTimeout(tick, delay) : undefined;
    };
    tick();
    return () => {
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [deadlineMs, offsetMs]);

  return current.countdown;
}
